'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import { extendConsentFallbackWindow, markConsentUnavailable } from '@extern/consent/state';
import Script from 'next/script';
import { isConsentDialogSuppressed } from '@/lib/consent/embedded-consent-dialog';
import { recordConsentEvent } from '@/lib/consent/record-consent-event';
import { getEnv } from '@/utils/env';

/**
 * No `data-blockingmode="auto"`. Cookiebot's auto-blocking rewrites `<script>` tags it recognizes
 * by third-party domain name, but our destinations don't match that model here: Segment and
 * PostHog are proxied through our own first-party subdomains specifically to survive ad blockers,
 * so Cookiebot's domain list won't recognize them, and TikTokPixelScript/PostHog are
 * React-mounted via next/script rather than static `<script>` tags in the initial HTML, which is
 * what the DOM-rewrite mechanism targets. Auto mode would silently gate nothing here while looking
 * configured. Cookiebot's job in this app is the banner UI and the consent record; actually gating
 * these destinations is our own code's job, and it is done in `@extern/consent` —
 * every vendor reads the answer from there rather than from this script.
 *
 * `data-cookiedomain=".extern.com"` puts Cookiebot's own cookie on the same domain as our
 * first-party visitor id, so a decision made on the marketing site is recognized here too — the
 * banner should not reopen for someone who already answered it during signup. Must be set on the
 * Webflow embed as well; if only one side carries it, that side's visitors still get asked twice.
 */
export function CookiebotScript() {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getEnv("COOKIEBOT_ID");
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const cbid = t0;
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (!cbid) {
        return;
      }
      extendConsentFallbackWindow();
      const handleDialogDisplay = () => {
        if (isConsentDialogSuppressed()) {
          window.Cookiebot?.hide?.();
          return;
        }
        recordConsentEvent({
          event: "shown",
          statisticsConsent: null,
          marketingConsent: null,
          preferencesConsent: null,
          cmpConfigVersion: cbid,
          cmpConsentStamp: null
        });
      };
      const handleDecision = decisionEvent => () => {
        if (!window.Cookiebot) {
          return;
        }
        const {
          statistics,
          marketing,
          preferences,
          stamp
        } = window.Cookiebot.consent;
        recordConsentEvent({
          event: decisionEvent,
          statisticsConsent: statistics,
          marketingConsent: marketing,
          preferencesConsent: preferences,
          cmpConfigVersion: cbid,
          cmpConsentStamp: stamp
        });
      };
      const handleAccept = handleDecision("accepted");
      const handleDecline = handleDecision("rejected");
      window.addEventListener("CookiebotOnDialogDisplay", handleDialogDisplay);
      window.addEventListener("CookiebotOnAccept", handleAccept);
      window.addEventListener("CookiebotOnDecline", handleDecline);
      return () => {
        window.removeEventListener("CookiebotOnDialogDisplay", handleDialogDisplay);
        window.removeEventListener("CookiebotOnAccept", handleAccept);
        window.removeEventListener("CookiebotOnDecline", handleDecline);
      };
    };
    t2 = [cbid];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  if (!cbid) {
    return null;
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Script data-cbid={cbid} data-cookiedomain=".extern.com" id="Cookiebot" onError={markConsentUnavailable} onLoad={extendConsentFallbackWindow} src="https://consent.cookiebot.com/uc.js" />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}